import type { ComponentProps, ReactNode } from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@repo/lib/cn';
import { CircleIcon } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';

import { Label } from '@/components/ui/label';

export function RadioGroup(radioGroupProps: ComponentProps<typeof RadioGroupPrimitive.Root>) {
  const { className, ...props } = radioGroupProps;
  return <RadioGroupPrimitive.Root data-slot="radio-group" className={cn('grid gap-3', className)} {...props} />;
}

export function RadioGroupItem(radioGroupItem: ComponentProps<typeof RadioGroupPrimitive.Item>) {
  const { className, ...props } = radioGroupItem;
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'relative border-border-primary bg-input dark:bg-input/35 focus-visible:bg-input-focus dark:focus-visible:bg-input-focus/25 shadow-xs hover:bg-accent text-brand focus-visible:border-border-primary-focus outline-0 outline-focus-ring focus-visible:outline-3 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive aria-invalid:border-destructive/75 flex size-5 rounded-full group/radio-group-item peer aspect-square shrink-0 border after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed disabled:opacity-60 transition-colors duration-150',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator data-slot="radio-group-indicator" className="group-aria-invalid/radio-group-item:text-destructive text-brand relative flex size-4.5 items-center justify-center">
        <CircleIcon className="absolute top-1/2 left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 fill-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

interface RadioGroupItemWithLabelProps extends ComponentProps<typeof RadioGroupPrimitive.Item> {
  label: string;
  description?: string | ReactNode;
}

export function RadioGroupItemWithLabel(radioGroupItemWithLabelPropsProps: RadioGroupItemWithLabelProps) {
  const { value, label, description, id, className, ...props } = radioGroupItemWithLabelPropsProps;
  const generatedId: string = useId();
  const baseId: string = id ?? `rg-${generatedId}-${value}`;
  const descId: string | undefined = description ? `${baseId}-desc` : undefined;

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <RadioGroupItem id={baseId} value={value} aria-describedby={descId} {...props} />
      <div className="grid grow gap-1">
        <Label htmlFor={baseId}>{label}</Label>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>
    </div>
  );
}

interface RadioGroupItemWithExpansionProps extends ComponentProps<typeof RadioGroupPrimitive.Item> {
  label: string;
  description?: string | ReactNode;
  expansionContent?: ReactNode;
  selectedValue?: string;
}

export function RadioGroupItemWithExpansion(radioGroupItemWithExpansionProps: RadioGroupItemWithExpansionProps) {
  const { value, label, description, expansionContent, selectedValue, id, className, ...props } = radioGroupItemWithExpansionProps;
  const isExpanded: boolean = selectedValue === value;
  const expansionRef = useRef<HTMLDivElement>(null);
  const generatedId: string = useId();
  const baseId: string = id ?? `rg-${generatedId}-${value}`;
  const descId: string | undefined = description ? `${baseId}-desc` : undefined;
  const expandId: string = `${baseId}-exp`;

  useEffect(() => {
    if (isExpanded && expansionRef.current) {
      const firstInput = expansionRef.current.querySelector('input, textarea, select');
      if (firstInput instanceof HTMLElement) {
        firstInput.focus();
      }
    }
  }, [isExpanded]);

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <RadioGroupItem id={baseId} value={value} aria-describedby={descId} aria-controls={expandId} {...props} />
        <div className="grow">
          <div className="grid grow gap-1">
            <Label htmlFor={baseId}>{label}</Label>
            {description && (
              <p id={descId} className="text-muted-foreground text-xs">
                {description}
              </p>
            )}
          </div>
          {expansionContent && (
            <div
              ref={expansionRef}
              role="region"
              id={expandId}
              aria-labelledby={baseId}
              className="grid transition-all ease-in-out data-[state=collapsed]:grid-rows-[0fr] data-[state=collapsed]:opacity-0 data-[state=expanded]:grid-rows-[1fr] data-[state=expanded]:opacity-100"
              data-state={isExpanded ? 'expanded' : 'collapsed'}
            >
              <div className="pointer-events-none -m-2 overflow-hidden p-2">
                <div className="pointer-events-auto mt-3">{expansionContent}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
